import { storeToRefs } from 'pinia'
import { IcsAttendee } from 'ts-ics'

import { useStoreContacts } from '@/entities/contact'

import { useCurrentUser } from '@/shared/composables/current-user'
import { Contact } from '@/shared/types/api'

import { normalizeCalendarEmail } from './lib'
import {
  buildCalendarAttendeeFromParticipant,
  resolveCalendarAttendeeToContact,
  resolveCalendarAttendeesToContacts,
  type CalendarAttendeeContactLookup,
} from './resolve-attendee'

export const useCalendarAttendeeContacts = () => {
  const { currentUser } = useCurrentUser()
  const contactsStore = useStoreContacts()
  const { allContacts } = storeToRefs(contactsStore)

  const findContactByEmail = (email: string): Contact | undefined => {
    const normalized = normalizeCalendarEmail(email)
    if (!normalized) {
      return undefined
    }

    if (normalizeCalendarEmail(currentUser.value?.email) === normalized) {
      return currentUser.value as Contact | undefined
    }

    return allContacts.value?.find(
      (contact) => normalizeCalendarEmail(contact.email) === normalized,
    )
  }

  const createLookup = (): CalendarAttendeeContactLookup => ({
    currentUser: currentUser.value ?? null,
    findByPServed: contactsStore.findByPServed,
    findByInternalNumber: contactsStore.findByInternalNumber,
    findContactByMobilePhone: contactsStore.findContactByMobilePhone,
    findContactByEmail,
    findByGuid: contactsStore.findByGuid,
    createExternalContact: contactsStore.createExternalContact,
  })

  const resolveAttendee = (attendee: IcsAttendee) => (
    resolveCalendarAttendeeToContact(attendee, createLookup())
  )

  const resolveAttendees = (attendees?: IcsAttendee[]) => (
    resolveCalendarAttendeesToContacts(attendees, createLookup())
  )

  const buildAttendee = (participantId: string) => (
    buildCalendarAttendeeFromParticipant(participantId, createLookup())
  )

  return {
    buildAttendee,
    findContactByEmail,
    resolveAttendee,
    resolveAttendees,
  }
}
